import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, Calendar, Clock, MapPin, Users, Home, Ticket, ChevronRight, Trash2, Plus, X, DollarSign, Save, Camera, Loader2, Pencil, HelpCircle, Search, UserPlus, Settings, LayoutGrid, Image as ImageIcon, Bookmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { authHeaders } from "@/lib/firebase";
import { eventCalendarDate, formatEventDate } from "@/lib/dates";
import MyMemories from "@/components/MyMemories";
import CreateEventPicker from "@/components/CreateEventPicker";
import SeriesScopeDialog from "@/components/SeriesScopeDialog";
import TopNav from "@/components/TopNav";
import heic2any from "heic2any";

// Geocode address using OpenStreetMap Nominatim (free, no API key)
const geocodeAddress = async (address, city) => {
  try {
    const query = encodeURIComponent(`${address}, ${city}`);
    const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1`, {
      headers: { 'User-Agent': 'Pouls App' }
    });
    const data = await response.json();
    if (data && data.length > 0) {
      return {
        latitude: parseFloat(data[0].lat),
        longitude: parseFloat(data[0].lon),
        displayName: data[0].display_name
      };
    }
    return null;
  } catch (error) {
    console.error("Geocoding failed:", error);
    return null;
  }
};

const sportImages = {
  basketball: "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800",
  football: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800",
  padel: "https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=800",
  volleyball: "https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=800",
};

const UK_CITIES = ["London", "Manchester", "Birmingham", "Liverpool", "Edinburgh", "Bristol", "Leeds", "Glasgow"];
const EU_CITIES = ["Berlin", "Amsterdam", "Paris"];
const currencyForCity = (city) => UK_CITIES.includes(city) ? "GBP" : EU_CITIES.includes(city) ? "EUR" : "USD";
const currencySymbol = (city) => UK_CITIES.includes(city) ? "£" : EU_CITIES.includes(city) ? "€" : "$";

const emptyForm = {
  sport: "basketball",
  title: "",
  description: "",
  date: "",
  time_start: "",
  time_end: "",
  location_name: "",
  city: "",
  address: "",
  latitude: 0,
  longitude: 0,
  max_players: 10,
  price: 0,
  currency: "GBP",
  image_url: sportImages.basketball,
  host_name: "",
  host_image_url: "",
  host_contact: "",
  meeting_info: "",
  skill_level: "all",
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, logout } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [myGames, setMyGames] = useState([]);
  const [editingGame, setEditingGame] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [refundAcknowledged, setRefundAcknowledged] = useState({});
  const [showEventPicker, setShowEventPicker] = useState(false);
  // Instagram-style profile content tab: "hosting" | "memories" | "saved"
  const [profileTab, setProfileTab] = useState("hosting");
  // Series scope picker (Google-Calendar-style "this only / all future"). Holds the pending action.
  const [scopeAction, setScopeAction] = useState(null);  // { type: "cancel" | "delete", game }

  // Wallet state
  const [walletBalances, setWalletBalances] = useState({ EUR: 0, GBP: 0, USD: 0 });
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [showWalletModal, setShowWalletModal] = useState(false);
  
  // Profile state
  const [profileStats, setProfileStats] = useState({ followers_count: 0, following_count: 0, games_hosted: 0, games_attended: 0, username: "", sports: [], custom_interests: "" });
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [showFollowingModal, setShowFollowingModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [editName, setEditName] = useState("");
  const [editUsername, setEditUsername] = useState("");
  const [editBio, setEditBio] = useState("");
  const [editSports, setEditSports] = useState([]);
  const [editCustomInterests, setEditCustomInterests] = useState("");
  const [editNotifyOnBooking, setEditNotifyOnBooking] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false);
  const [profilePicPreview, setProfilePicPreview] = useState(null);

  const SPORTS_OPTIONS = [
    { name: "Football", emoji: "⚽" },
    { name: "Basketball", emoji: "🏀" },
    { name: "Tennis", emoji: "🎾" },
    { name: "Padel", emoji: "🏓" },
    { name: "Running", emoji: "🏃" },
    { name: "Volleyball", emoji: "🏐" },
    { name: "Swimming", emoji: "🏊" },
    { name: "Cycling", emoji: "🚴" },
    { name: "Golf", emoji: "⛳" },
    { name: "Boxing", emoji: "🥊" },
    { name: "Yoga", emoji: "🧘" },
    { name: "CrossFit", emoji: "🏋️" },
    { name: "Badminton", emoji: "🏸" },
    { name: "Table Tennis", emoji: "🏓" },
    { name: "Cricket", emoji: "🏏" },
    { name: "Rugby", emoji: "🏉" },
    { name: "Hockey", emoji: "🏒" },
    { name: "Climbing", emoji: "🧗" },
    { name: "Hiking", emoji: "🥾" },
    { name: "Surfing", emoji: "🏄" },
    { name: "Skiing", emoji: "⛷️" },
    { name: "Skateboarding", emoji: "🛹" },
  ];

  const getSportEmoji = (sportName) => {
    const sport = SPORTS_OPTIONS.find(s => s.name === sportName);
    return sport ? sport.emoji : "🎯";
  };

  const user = location.state?.user || authUser;

  const fetchReservations = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const response = await fetch(`${API}/reservations`, { headers });
      if (response.ok) {
        const data = await response.json();
        setReservations(data);
      }
    } catch (error) {
      console.error("Failed to fetch reservations:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyGames = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const response = await fetch(`${API}/my-games`, { headers });
      if (response.ok) {
        setMyGames(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch my games:", error);
    }
  }, []);

  const fetchProfileStats = useCallback(async () => {
    if (!user?.user_id) return;
    try {
      const headers = await authHeaders();
      const response = await fetch(`${API}/users/${user.user_id}/profile`, { headers });
      if (response.ok) {
        const data = await response.json();
        setProfileStats({
          followers_count: data.followers_count || 0,
          following_count: data.following_count || 0,
          games_hosted: data.games_hosted || 0,
          games_attended: data.games_attended || 0,
          bio: data.bio || "",
          username: data.username || "",
          sports: data.sports || [],
          custom_interests: data.custom_interests || "",
          email: data.email || user?.email || "",
          picture: data.picture || user?.picture
        });
        setEditName(data.name || user?.name || "");
        setEditUsername(data.username || "");
        setEditBio(data.bio || "");
        setEditSports(data.sports || []);
        setEditCustomInterests(data.custom_interests || "");
        setEditNotifyOnBooking(data.notify_email_on_booking !== false);
      }
    } catch (error) {
      console.error("Failed to fetch profile stats:", error);
    }
  }, [user?.user_id, user?.name, user?.picture]);

  const fetchFollowers = async () => {
    if (!user?.user_id) return;
    try {
      const headers = await authHeaders();
      const response = await fetch(`${API}/users/${user.user_id}/followers`, { headers });
      if (response.ok) {
        setFollowers(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch followers:", error);
    }
  };

  const fetchFollowing = async () => {
    if (!user?.user_id) return;
    try {
      const headers = await authHeaders();
      const response = await fetch(`${API}/users/${user.user_id}/following`, { headers });
      if (response.ok) {
        setFollowing(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch following:", error);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const response = await fetch(`${API}/users/me/profile`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ 
          name: editName, 
          username: editUsername,
          bio: editBio,
          sports: editSports,
          custom_interests: editCustomInterests,
          notify_email_on_booking: editNotifyOnBooking,
        })
      });
      
      if (response.ok) {
        setProfileStats(prev => ({ 
          ...prev, 
          bio: editBio, 
          username: editUsername,
          sports: editSports,
          custom_interests: editCustomInterests
        }));
        setShowEditProfileModal(false);
        toast.success("Profile updated!");
        // Refresh to update name across the app
        window.location.reload();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update profile");
      }
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleProfilePicUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    console.log("[profile-pic] selected", { name: file.name, type: file.type, size: file.size });

    // Check for HEIC and convert on frontend if needed
    let uploadFile = file;
    const isHeic = file.type === "image/heic" || file.type === "image/heif" || 
                   file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif");
    
    if (isHeic) {
      toast.info("Converting iPhone photo...");
      try {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.85
        });
        const jpegBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        uploadFile = new File([jpegBlob], file.name.replace(/\.heic$/i, ".jpg").replace(/\.heif$/i, ".jpg"), { type: "image/jpeg" });
        console.log("[profile-pic] HEIC converted", { size: uploadFile.size });
      } catch (convErr) {
        console.error("[profile-pic] HEIC conversion error:", convErr);
        toast.error("Failed to convert iPhone photo. Try a JPEG instead.");
        return;
      }
    }

    if (!uploadFile.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (uploadFile.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }

    // Show preview
    setProfilePicPreview(URL.createObjectURL(uploadFile));
    setUploadingProfilePic(true);

    try {
      const fd = new FormData();
      fd.append("file", uploadFile, uploadFile.name);

      const { getAuthToken } = await import("@/lib/firebase");
      const token = await getAuthToken();
      if (!token) {
        toast.error("Not signed in. Please sign in again.");
        setProfilePicPreview(null);
        return;
      }

      // Use XMLHttpRequest to bypass any fetch interceptors (e.g., visual-edits)
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/users/me/profile-picture`, true);
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.timeout = 60000;

        xhr.onload = () => {
          console.log("[profile-pic] xhr complete", { status: xhr.status, response: xhr.responseText?.slice(0, 500) });
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (parseErr) {
              reject(new Error("Server returned invalid response"));
            }
          } else {
            let errorMsg = `Upload failed (${xhr.status})`;
            try {
              const errData = JSON.parse(xhr.responseText);
              if (errData.detail) errorMsg = typeof errData.detail === "string" ? errData.detail : JSON.stringify(errData.detail);
            } catch (e) {
              if (xhr.responseText) errorMsg = xhr.responseText.slice(0, 200);
            }
            reject(new Error(errorMsg));
          }
        };
        xhr.onerror = () => reject(new Error("Network error - check your connection"));
        xhr.ontimeout = () => reject(new Error("Upload timed out - try a smaller image"));
        xhr.send(fd);
      });

      // Success - update UI
      setProfileStats(prev => ({ ...prev, picture: data.picture }));
      setProfilePicPreview(null); // clear preview, real URL takes over
      toast.success("Profile picture updated!");
      // Re-fetch to ensure everything stays in sync
      fetchProfileStats();
    } catch (error) {
      console.error("[profile-pic] upload error:", error);
      toast.error(error.message || "Failed to upload image");
      setProfilePicPreview(null);
    } finally {
      setUploadingProfilePic(false);
    }
  };

  const toggleSport = (sportName) => {
    setEditSports(prev => 
      prev.includes(sportName) 
        ? prev.filter(s => s !== sportName)
        : [...prev, sportName]
    );
  };

  const fetchWallet = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const r = await fetch(`${API}/me/wallet`, { headers });
      if (r.ok) {
        const data = await r.json();
        setWalletBalances(data.balances || { EUR: 0, GBP: 0, USD: 0 });
        setWalletTransactions(data.transactions || []);
      }
    } catch (e) {
      console.error("Failed to load wallet", e);
    }
  }, []);

  const handleFollowUser = async (targetUserId, isCurrentlyFollowing) => {
    try {
      const headers = await authHeaders();
      const method = isCurrentlyFollowing ? "DELETE" : "POST";
      const response = await fetch(`${API}/users/${targetUserId}/follow`, { method, headers });
      
      if (response.ok) {
        const data = await response.json();
        setFollowers(prev => prev.map(u => 
          u.user_id === targetUserId ? { ...u, is_following: data.is_following } : u
        ));
        setFollowing(prev => prev.map(u => 
          u.user_id === targetUserId ? { ...u, is_following: data.is_following } : u
        ));
        toast.success(data.is_following ? "Following!" : "Unfollowed");
      }
    } catch (error) {
      toast.error("Failed to update follow status");
    }
  };

  useEffect(() => { fetchReservations(); fetchMyGames(); fetchProfileStats(); fetchWallet(); }, [fetchReservations, fetchMyGames, fetchProfileStats, fetchWallet]);

  // Backward-compat: redirect any old ?create=… query params to the new dedicated pages.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("create") === "1") navigate("/create/game", { replace: true });
    else if (params.get("create_series") === "1") navigate("/create/series", { replace: true });
    else if (params.get("create_trip") === "1") navigate("/create/trip", { replace: true });
  }, [location.search, navigate]);

  const handleCancelReservation = async (reservationId) => {
    setCancelling(reservationId);
    try {
      const headers = await authHeaders();
      const response = await fetch(`${API}/reservations/${reservationId}`, {
        method: "DELETE",
        headers
      });
      if (response.ok) {
        toast.success("Reservation cancelled");
        fetchReservations();
      } else {
        toast.error("Failed to cancel reservation");
      }
    } catch (error) {
      toast.error("Failed to cancel reservation");
    } finally {
      setCancelling(null);
    }
  };

  const handleDeleteHostedGame = async (gameId) => {
    try {
      const headers = await authHeaders();
      const response = await fetch(`${API}/games/${gameId}`, { method: "DELETE", headers });
      if (response.ok) {
        toast.success("Game deleted");
        setMyGames(prev => prev.filter(g => g.game_id !== gameId));
        fetchReservations();
      } else {
        const detail = await response.json().then(d => d.detail).catch(() => null);
        toast.error(detail || "Failed to delete game");
      }
    } catch (error) {
      toast.error("Failed to delete game");
    }
  };

  // If the game is part of a series, open the scope picker; otherwise run the action directly.
  const requestGameAction = (type, game) => {
    if (game?.series_id) {
      setScopeAction({ type, game });
      return;
    }
    if (type === "cancel") handleCancelHostedGame(game.game_id);
    if (type === "delete") handleDeleteHostedGame(game.game_id);
  };

  const runScopedAction = async (scope) => {
    if (!scopeAction) return;
    const { type, game } = scopeAction;
    setScopeAction(null);

    try {
      const headers = await authHeaders();

      if (scope === "all_future") {
        // Cancel the WHOLE series (deletion of a series isn't a thing — we cancel)
        const response = await fetch(`${API}/series/${game.series_id}/cancel`, { method: "POST", headers });
        if (response.ok) {
          const data = await response.json();
          const parts = [];
          if (data.future_sessions_cancelled) parts.push(`${data.future_sessions_cancelled} future session${data.future_sessions_cancelled !== 1 ? "s" : ""} cancelled`);
          if (data.pass_holders_refunded) parts.push(`${data.pass_holders_refunded} pass holder${data.pass_holders_refunded !== 1 ? "s" : ""} credited`);
          toast.success(parts.length ? `Series cancelled — ${parts.join(", ")}` : "Series cancelled");
          // Mark every game in this series as cancelled locally
          setMyGames(prev => prev.map(g => g.series_id === game.series_id ? { ...g, status: "cancelled" } : g));
          fetchReservations();
        } else {
          const detail = await response.json().then(d => d.detail).catch(() => null);
          toast.error(detail || "Failed to cancel series");
        }
        return;
      }

      // scope === "single" → run on this game only
      if (type === "cancel") {
        await handleCancelHostedGame(game.game_id);
      } else if (type === "delete") {
        await handleDeleteHostedGame(game.game_id);
      }
    } catch (error) {
      toast.error("Action failed");
    }
  };

  const handleCancelHostedGame = async (gameId) => {
    try {
      const headers = await authHeaders();
      const response = await fetch(`${API}/games/${gameId}/cancel`, { method: "POST", headers });
      if (response.ok) {
        const data = await response.json();
        const parts = [];
        if (data.notified) parts.push(`${data.notified} attendee${data.notified !== 1 ? "s" : ""} notified`);
        if (data.refunded) parts.push(`${data.refunded} refund${data.refunded !== 1 ? "s" : ""} issued`);
        toast.success(parts.length ? `Game cancelled — ${parts.join(", ")}` : "Game cancelled");
        setMyGames(prev => prev.map(g => g.game_id === gameId ? { ...g, status: "cancelled" } : g));
        fetchReservations();
      } else {
        const detail = await response.json().then(d => d.detail).catch(() => null);
        toast.error(detail || "Failed to cancel game");
      }
    } catch (error) {
      toast.error("Failed to cancel game");
    }
  };

  const handleImageUpload = async (e) => {
    let file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/") && !file.name.toLowerCase().endsWith(".heic") && !file.name.toLowerCase().endsWith(".heif")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB");
      return;
    }

    // Convert HEIC/HEIF to JPEG (iPhone photos)
    const isHeic = file.type === "image/heic" || file.type === "image/heif" || 
                   file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif");
    
    if (isHeic) {
      toast.info("Converting iPhone photo...");
      try {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.85
        });
        const jpegBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        file = new File([jpegBlob], file.name.replace(/\.heic$/i, ".jpg").replace(/\.heif$/i, ".jpg"), { type: "image/jpeg" });
      } catch (convErr) {
        console.error("HEIC conversion error:", convErr);
        toast.error("Failed to convert iPhone photo. Try taking a screenshot instead.");
        return;
      }
    }

    // Show local preview immediately
    setImagePreview(URL.createObjectURL(file));
    setUploadingImage(true);

    try {
      const { getAuthToken } = await import("@/lib/firebase");
      const token = await getAuthToken();

      // Use XMLHttpRequest to bypass visual-edits fetch interceptor
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/upload/game-image`);
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(data);
            } else {
              reject(new Error(data.detail || "Upload failed"));
            }
          } catch {
            reject(new Error("Upload failed"));
          }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        const body = new FormData();
        body.append("file", file);
        xhr.send(body);
      });

      const imageUrl = `${API}/files/${result.storage_path}`;
      setEditFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success("Image uploaded");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.message || "Upload failed");
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleEditGame = async () => {
    if (!editFormData.title || !editFormData.date || !editFormData.time_start || !editFormData.time_end || !editFormData.city || !editFormData.location_name) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editFormData,
        host_name: editFormData.host_name || user?.name || "Host",
        host_image_url: editFormData.host_image_url || user?.picture || "",
        currency: UK_CITIES.includes(editFormData.city) ? "GBP" : "USD",
        date: new Date(editFormData.date).toISOString(),
      };

      const headers = await authHeaders({ "Content-Type": "application/json" });
      const response = await fetch(`${API}/games/${editingGame.game_id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success("Game updated!");
        setShowEditModal(false);
        setEditingGame(null);
        fetchMyGames(); // Refresh the list
      } else {
        const cloned = response.clone();
        const detail = await cloned.json().then(d => d.detail).catch(() => null);
        toast.error(detail || "Failed to update game");
      }
    } catch (error) {
      toast.error("Failed to update game");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully");
    navigate("/");
  };

  const formatDate = (dateString) => {
    const date = eventCalendarDate(dateString);
    if (!date) return "";
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (date.toDateString() === now.toDateString()) return "Today";
    if (date.toDateString() === tomorrow.toDateString()) return "Tomorrow";
    return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  const getSportBadgeClass = (sport) => {
    switch (sport) {
      case "basketball": return "sport-badge-basketball";
      case "football": return "sport-badge-football";
      case "padel": return "sport-badge-padel";
      case "volleyball": return "sport-badge-volleyball";
      default: return "bg-primary/20 text-primary";
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <TopNav onHostClick={() => setShowEventPicker(true)} />

      <main className="container mx-auto px-4 py-6">
        {/* Always-mounted hidden file input so it works even when the modal is closed */}
        <input
          type="file"
          accept="image/*,.heic,.heif"
          onChange={handleProfilePicUpload}
          className="hidden"
          id="profile-pic-input"
          data-testid="profile-pic-input"
        />
        {/* Profile Section - Instagram Style */}
        <div className="mb-8">
          <div className="flex gap-6 sm:gap-10 items-start">
            {/* Avatar on left - click to upload */}
            <button
              type="button"
              onClick={() => document.getElementById('profile-pic-input')?.click()}
              disabled={uploadingProfilePic}
              className="relative group shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="profile-avatar-upload-btn"
              aria-label="Change profile picture"
            >
              <Avatar className="w-20 h-20 sm:w-24 sm:h-24 ring-2 ring-border">
                <AvatarImage src={profilePicPreview || profileStats.picture || user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-primary/20 text-primary text-2xl sm:text-3xl">{getInitials(user?.name)}</AvatarFallback>
              </Avatar>
              <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                {uploadingProfilePic ? (
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                ) : (
                  <Camera className="w-6 h-6 text-white" />
                )}
              </span>
              {uploadingProfilePic && (
                <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </span>
              )}
            </button>
            
            {/* Info on right */}
            <div className="flex-1 min-w-0">
              {/* Username row with settings icon */}
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-xl font-semibold truncate">
                  {profileStats.username || user?.name?.split(" ")[0]?.toLowerCase() || "user"}
                </h2>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1.5 hover:bg-muted rounded-lg transition-colors"
                      data-testid="profile-settings-menu-btn"
                      aria-label="Account menu"
                    >
                      <Settings className="w-5 h-5 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52">
                    <DropdownMenuItem
                      onClick={() => setShowEditProfileModal(true)}
                      data-testid="menu-edit-profile"
                    >
                      <Pencil className="w-4 h-4 mr-2" /> Edit profile
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/host")}
                      data-testid="menu-host-os"
                    >
                      <LayoutGrid className="w-4 h-4 mr-2" /> Host dashboard
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/30"
                      data-testid="menu-logout"
                    >
                      <LogOut className="w-4 h-4 mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              {/* Full name */}
              <p className="font-medium mb-2">{user?.name}</p>
              
              {/* Stats row */}
              <div className="flex gap-4 sm:gap-6 mb-3">
                <div className="text-center sm:text-left">
                  <span className="font-bold">{profileStats.games_attended}</span>
                  <span className="text-muted-foreground ml-1">games</span>
                </div>
                <button onClick={() => { fetchFollowers(); setShowFollowersModal(true); }} className="text-center sm:text-left hover:opacity-70">
                  <span className="font-bold">{profileStats.followers_count}</span>
                  <span className="text-muted-foreground ml-1">followers</span>
                </button>
                <button onClick={() => { fetchFollowing(); setShowFollowingModal(true); }} className="text-center sm:text-left hover:opacity-70">
                  <span className="font-bold">{profileStats.following_count}</span>
                  <span className="text-muted-foreground ml-1">following</span>
                </button>
              </div>
              
              {/* Bio */}
              {profileStats.bio && (
                <p className="text-sm whitespace-pre-line mb-2">{profileStats.bio}</p>
              )}
              
              {/* Sports with emojis */}
              {profileStats.sports && profileStats.sports.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {profileStats.sports.map(sport => (
                    <Badge key={sport} variant="secondary" className="text-xs">
                      {getSportEmoji(sport)} {sport}
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Custom interests */}
              {profileStats.custom_interests && (
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Also interested in:</span> {profileStats.custom_interests}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="mb-6">
          <p className="text-muted-foreground">
            {reservations.length === 0
              ? "You haven't joined any games yet. Find one or host your own!"
              : `You have ${reservations.length} upcoming game${reservations.length !== 1 ? "s" : ""}`}
          </p>
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          <Button onClick={() => navigate("/")} className="btn-glow bg-primary text-primary-foreground hover:bg-primary/90 font-display" data-testid="find-games-btn">
            <Ticket className="w-4 h-4 mr-2" />
            Find Games
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>

          <Button
            onClick={() => setShowEventPicker(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-display"
            data-testid="create-game-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Event
          </Button>

          <Button
            variant="outline"
            className="border-border text-muted-foreground hover:text-foreground hover:bg-muted font-display"
            onClick={() => window.location.href = "mailto:mert.elqi@gmail.com?subject=Pouls%20Support%20Request"}
            data-testid="support-btn"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            Support
          </Button>
        </div>

        {/* Wallet card - only shown if user has any balance */}
        {(walletBalances.EUR > 0 || walletBalances.GBP > 0 || walletBalances.USD > 0) && (
          <Card 
            className="border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors cursor-pointer mb-6"
            onClick={() => setShowWalletModal(true)}
            data-testid="wallet-card"
          >
            <div className="p-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <DollarSign className="w-4 h-4 text-primary" />
                  <span className="font-display uppercase tracking-wider text-xs">Pouls Wallet</span>
                </div>
                <div className="flex items-baseline gap-3 flex-wrap">
                  {walletBalances.EUR > 0 && <span className="text-xl font-bold">€{walletBalances.EUR.toFixed(2)}</span>}
                  {walletBalances.GBP > 0 && <span className="text-xl font-bold">£{walletBalances.GBP.toFixed(2)}</span>}
                  {walletBalances.USD > 0 && <span className="text-xl font-bold">${walletBalances.USD.toFixed(2)}</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Auto-applied at checkout</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          </Card>
        )}

        {/* Instagram-style profile content tabs */}
        <div className="border-t border-border mt-6" data-testid="profile-content-tabs">
          <div className="flex items-stretch justify-around">
            {[
              { id: "hosting", label: "Hosting", Icon: LayoutGrid, testid: "tab-hosting" },
              { id: "memories", label: "Memories", Icon: ImageIcon, testid: "tab-memories" },
              { id: "saved", label: "Saved", Icon: Bookmark, testid: "tab-saved", private: true },
            ].map((t) => {
              const active = profileTab === t.id;
              const Icon = t.Icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setProfileTab(t.id)}
                  data-testid={t.testid}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 -mt-px border-t-2 transition-colors ${
                    active ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                  aria-pressed={active}
                >
                  <Icon className={`w-4 h-4 ${active ? "" : "opacity-70"}`} />
                  <span className="text-xs sm:text-sm font-medium tracking-wide uppercase">{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Reservations — "Saved" tab (private) */}
        {profileTab === "saved" && (
        <div className="space-y-4 mt-6">

          {loading ? (
            <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="skeleton-pulse h-32 rounded-xl" />)}</div>
          ) : reservations.length === 0 ? (
            <Card className="glass-card">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Calendar className="w-8 h-8 text-muted-foreground" />
                </div>
                <h4 className="font-display text-lg mb-2">No games yet</h4>
                <p className="text-muted-foreground text-sm text-center mb-4">Join a game or host your own</p>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/")} className="border-primary/30 hover:bg-primary/10">Browse Games</Button>
                  <Button onClick={() => setShowEventPicker(true)} className="bg-primary text-primary-foreground hover:bg-primary/90">Create Event</Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4" data-testid="reservations-list">
              {reservations.map((reservation) => (
                <Card 
                  key={reservation.reservation_id} 
                  className="glass-card overflow-hidden cursor-pointer hover:border-primary/30 transition-colors" 
                  data-testid={`reservation-${reservation.reservation_id}`}
                >
                  <div className="flex flex-col sm:flex-row" onClick={() => navigate(`/game/${reservation.game?.game_id}`)}>
                    {reservation.game && (
                      <div className="relative w-full sm:w-40 h-32 sm:h-auto shrink-0">
                        <img src={reservation.game.image_url} alt={reservation.game.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 card-image-overlay sm:bg-gradient-to-r sm:from-transparent sm:to-card" />
                        <div className="absolute top-2 left-2">
                          <span className={`sport-badge ${getSportBadgeClass(reservation.game.sport)}`}>{reservation.game.sport}</span>
                        </div>
                      </div>
                    )}
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-display text-lg font-semibold mb-2 line-clamp-1">{reservation.game?.title || "Game"}</h4>
                          <div className="space-y-1.5 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-primary shrink-0" />
                              <span>{formatDate(reservation.game?.date)}</span>
                              <span className="text-border">·</span>
                              <Clock className="w-4 h-4 shrink-0" />
                              <span>{reservation.game?.time_start} - {reservation.game?.time_end}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-primary shrink-0" />
                              <span className="line-clamp-1">{reservation.game?.location_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 shrink-0" />
                              <span>{reservation.game?.current_players}/{reservation.game?.max_players} players</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                        <Badge variant="outline" className="text-foreground border-border bg-card">Confirmed</Badge>
                        {reservation.game?.price > 0 && (
                          <span className="text-sm font-medium">{reservation.game.currency === "GBP" ? "£" : "$"}{reservation.game.price}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Memories — public tab */}
        {profileTab === "memories" && (
          <div className="mt-6">
            <MyMemories />
          </div>
        )}

        {/* Hosting — public tab. For deep stats and management, "/host" has the full Event OS. */}
        {profileTab === "hosting" && (
          <div className="mt-6" data-testid="profile-hosting-tab">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-muted-foreground">
                {myGames.length === 0 ? "Nothing here yet" : `${myGames.length} game${myGames.length !== 1 ? "s" : ""} you host`}
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/host")}
                className="text-xs gap-1"
                data-testid="profile-open-host-os"
              >
                Open Host OS <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
            {myGames.length === 0 ? (
              <Card className="border-dashed border-border p-8 text-center">
                <p className="text-sm text-muted-foreground mb-3">
                  You haven&apos;t hosted any one-off games yet. Start one and it&apos;ll appear here for everyone to see.
                </p>
                <Button onClick={() => setShowEventPicker(true)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="profile-empty-create">
                  <Plus className="w-4 h-4 mr-1" /> Host an event
                </Button>
              </Card>
            ) : (
              <div className="mt-1" data-testid="my-games-section">
            <div className="space-y-3">
              {myGames.map((g) => (
                <Card key={g.game_id} className="border-border bg-card hover:border-primary/30 transition-colors" data-testid={`my-game-${g.game_id}`}>
                  <div className="p-4 flex items-center gap-4">
                    <img 
                      src={g.image_url} 
                      alt={g.title} 
                      className="w-16 h-16 rounded-lg object-cover shrink-0 cursor-pointer" 
                      onClick={() => navigate(`/game/${g.game_id}`)}
                    />
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/game/${g.game_id}`)}>
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate">{g.title}</p>
                        {g.status === "cancelled" && (
                          <Badge variant="outline" className="text-xs text-orange-500 border-orange-500/40 bg-orange-500/10 shrink-0">Cancelled</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{formatEventDate(g.date, { day: "numeric", month: "short" })}</span>
                        <span className="text-border">·</span>
                        <MapPin className="w-3.5 h-3.5" />
                        <span className="truncate">{g.city}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-0.5">
                        <Users className="w-3.5 h-3.5" />
                        <span>{g.current_players}/{g.max_players} players</span>
                        {g.price > 0 && (
                          <>
                            <span className="text-border">·</span>
                            <span>{g.currency === "GBP" ? "£" : "$"}{g.price}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingGame(g);
                        setEditFormData({
                          sport: g.sport,
                          title: g.title,
                          description: g.description,
                          date: g.date ? String(g.date).slice(0, 10) : "",
                          time_start: g.time_start,
                          time_end: g.time_end,
                          location_name: g.location_name,
                          city: g.city,
                          address: g.address,
                          latitude: g.latitude,
                          longitude: g.longitude,
                          max_players: g.max_players,
                          price: g.price,
                          currency: g.currency,
                          image_url: g.image_url,
                          host_name: g.host_name,
                          host_image_url: g.host_image_url,
                          skill_level: g.skill_level,
                          host_contact: g.host_contact || "",
                          meeting_info: g.meeting_info || "",
                        });
                        setImagePreview(g.image_url);
                        setShowEditModal(true);
                      }}
                      data-testid={`edit-game-${g.game_id}`}
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    {g.status !== "cancelled" && (() => {
                      // Allow cancel if the event day hasn't ended yet (matches public listing logic)
                      const eventDay = eventCalendarDate(g.date);
                      if (!eventDay) return true;
                      eventDay.setHours(23, 59, 59, 999);
                      return eventDay >= new Date();
                    })() && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="shrink-0 text-orange-500 border-orange-500/40 hover:bg-orange-500/10"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`cancel-game-${g.game_id}`}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Cancel
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-card border-border">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Cancel this event?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will mark "{g.title}" as cancelled, notify all confirmed attendees by email, and automatically refund every paid attendee. This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Keep Event</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => requestGameAction("cancel", g)}
                              className="bg-orange-500 text-white hover:bg-orange-600"
                              data-testid={`confirm-cancel-game-${g.game_id}`}
                            >
                              Cancel & Notify Attendees
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`delete-game-${g.game_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-card border-border">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this game?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This permanently deletes "{g.title}" and all reservations. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => requestGameAction("delete", g)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid={`confirm-delete-game-${g.game_id}`}
                          >
                            Delete Game
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <ChevronRight 
                      className="w-5 h-5 text-muted-foreground shrink-0 cursor-pointer" 
                      onClick={() => navigate(`/game/${g.game_id}`)}
                    />
                  </div>
                </Card>
              ))}
            </div>
          </div>
            )}
          </div>
        )}
      </main>

      {/* Wallet History Modal */}
      <Dialog open={showWalletModal} onOpenChange={setShowWalletModal}>
        <DialogContent className="bg-card border-border max-w-md max-h-[80vh] overflow-y-auto" data-testid="wallet-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Pouls Wallet
            </DialogTitle>
            <DialogDescription>Your credit balance and transaction history</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Balances */}
            <div className="rounded-xl bg-primary/10 border border-primary/30 p-4 space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Available balance</p>
              {walletBalances.EUR > 0 && <p className="text-2xl font-bold">€{walletBalances.EUR.toFixed(2)}</p>}
              {walletBalances.GBP > 0 && <p className="text-2xl font-bold">£{walletBalances.GBP.toFixed(2)}</p>}
              {walletBalances.USD > 0 && <p className="text-2xl font-bold">${walletBalances.USD.toFixed(2)}</p>}
              {walletBalances.EUR === 0 && walletBalances.GBP === 0 && walletBalances.USD === 0 && (
                <p className="text-2xl font-bold text-muted-foreground">€0.00</p>
              )}
              <p className="text-xs text-muted-foreground pt-1">Credit is automatically applied when you book a paid game in the same currency.</p>
            </div>

            {/* Transactions */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">History</h4>
              {walletTransactions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No transactions yet</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {walletTransactions.map(t => {
                    const sym = t.currency === "EUR" ? "€" : t.currency === "GBP" ? "£" : "$";
                    const isCredit = t.type === "credit";
                    return (
                      <div key={t.transaction_id} className="flex items-start justify-between gap-2 p-3 rounded-lg bg-muted/40 border border-border">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm truncate">{t.reason}</p>
                          <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                        </div>
                        <p className={`text-sm font-semibold whitespace-nowrap ${isCredit ? "text-primary" : "text-muted-foreground"}`}>
                          {isCredit ? "+" : "-"}{sym}{t.amount.toFixed(2)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Event Picker */}
      <CreateEventPicker open={showEventPicker} onOpenChange={setShowEventPicker} />

      {/* Series scope picker (Google-Calendar-style "this only / all future") */}
      <SeriesScopeDialog
        open={!!scopeAction}
        onOpenChange={(v) => { if (!v) setScopeAction(null); }}
        title={scopeAction?.type === "delete" ? "Delete a series session?" : "Cancel a series session?"}
        description="This session is part of a recurring series. What do you want to apply this to?"
        actionLabel={scopeAction?.type === "delete" ? "Delete" : "Cancel session(s)"}
        destructive={scopeAction?.type === "delete"}
        onConfirm={runScopedAction}
      />


      {/* Edit Game Modal */}
      <Dialog open={showEditModal} onOpenChange={(open) => { setShowEditModal(open); if (!open) setEditingGame(null); }}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-game-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Edit Game</DialogTitle>
            <DialogDescription>Update your game details</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label>Sport *</Label>
              <Select value={editFormData.sport} onValueChange={(v) => setEditFormData(prev => ({ ...prev, sport: v }))}>
                <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="basketball">Basketball</SelectItem>
                  <SelectItem value="football">Football</SelectItem>
                  <SelectItem value="padel">Padel</SelectItem>
                  <SelectItem value="volleyball">Volleyball</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Game Image Upload */}
            <div>
              <Label>Game Image</Label>
              <div className="mt-1 relative rounded-lg overflow-hidden border border-border bg-muted/30" style={{ aspectRatio: "16/9" }}>
                <img
                  src={imagePreview || editFormData.image_url}
                  alt="Game preview"
                  className="w-full h-full object-cover"
                />
                {uploadingImage && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  </div>
                )}
                <label
                  htmlFor="edit-game-image-upload"
                  className="absolute bottom-2 right-2 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 hover:bg-black/90 text-white text-xs font-medium cursor-pointer transition-colors"
                >
                  <Camera className="w-3.5 h-3.5" />
                  {uploadingImage ? "Uploading..." : "Change Image"}
                </label>
                <input
                  id="edit-game-image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                />
              </div>
            </div>

            <div>
              <Label>Game Title *</Label>
              <Input value={editFormData.title} onChange={(e) => setEditFormData(prev => ({ ...prev, title: e.target.value }))} placeholder="e.g. Sunday Morning Hoops" className="bg-muted/50" />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea value={editFormData.description} onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))} placeholder="Tell people what to expect..." className="bg-muted/50" rows={2} />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={editFormData.date} onChange={(e) => setEditFormData(prev => ({ ...prev, date: e.target.value }))} className="bg-muted/50" />
              </div>
              <div>
                <Label>Start *</Label>
                <Input type="time" value={editFormData.time_start} onChange={(e) => setEditFormData(prev => ({ ...prev, time_start: e.target.value }))} className="bg-muted/50" />
              </div>
              <div>
                <Label>End *</Label>
                <Input type="time" value={editFormData.time_end} onChange={(e) => setEditFormData(prev => ({ ...prev, time_end: e.target.value }))} className="bg-muted/50" />
              </div>
            </div>

            <div>
              <Label>City *</Label>
              <Select value={editFormData.city} onValueChange={(v) => setEditFormData(prev => ({ ...prev, city: v, currency: currencyForCity(v) }))}>
                <SelectTrigger className="bg-muted/50"><SelectValue placeholder="Select city" /></SelectTrigger>
                <SelectContent>
                  {["London", "Manchester", "Birmingham", "Liverpool", "Edinburgh", "Bristol", "Leeds", "Glasgow", "New York", "Los Angeles", "Chicago", "Miami", "Berlin", "Amsterdam", "Paris"].map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Venue Name *</Label>
              <Input value={editFormData.location_name} onChange={(e) => setEditFormData(prev => ({ ...prev, location_name: e.target.value }))} placeholder="e.g. Brixton Recreation Centre" className="bg-muted/50" />
            </div>

            <div>
              <Label>Address *</Label>
              <div className="flex gap-2">
                <Input 
                  value={editFormData.address} 
                  onChange={(e) => setEditFormData(prev => ({ ...prev, address: e.target.value, latitude: 0, longitude: 0 }))} 
                  placeholder="Full address" 
                  className="bg-muted/50 flex-1" 
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (!editFormData.address) {
                      toast.error("Enter an address first");
                      return;
                    }
                    toast.loading("Looking up address...", { id: "geocode-edit" });
                    const result = await geocodeAddress(editFormData.address, editFormData.city);
                    if (result) {
                      setEditFormData(prev => ({ ...prev, latitude: result.latitude, longitude: result.longitude }));
                      toast.success("Location found!", { id: "geocode-edit" });
                    } else {
                      toast.error("Couldn't find that address", { id: "geocode-edit" });
                    }
                  }}
                  className="shrink-0"
                >
                  <Search className="w-4 h-4 mr-1" />
                  Find
                </Button>
              </div>
              {/* Map preview when coordinates are set */}
              {editFormData.latitude !== 0 && editFormData.longitude !== 0 && (
                <div className="mt-2 rounded-lg overflow-hidden border border-border">
                  <iframe
                    title="Location Preview"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${editFormData.longitude - 0.003},${editFormData.latitude - 0.002},${editFormData.longitude + 0.003},${editFormData.latitude + 0.002}&layer=mapnik&marker=${editFormData.latitude},${editFormData.longitude}`}
                    className="w-full h-28"
                    style={{ border: 0 }}
                  />
                  <p className="text-xs text-green-600 px-2 py-1.5 bg-green-50 dark:bg-green-950/30 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Location verified - this is where the pin will show
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Max Players *</Label>
                <Input type="number" min="2" max="50" value={editFormData.max_players} onChange={(e) => setEditFormData(prev => ({ ...prev, max_players: parseInt(e.target.value) || 2 }))} className="bg-muted/50" />
              </div>
              <div>
                <Label>Price ({currencySymbol(editFormData.city)})</Label>
                <Input type="number" min="0" step="0.5" value={editFormData.price} onChange={(e) => setEditFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))} className="bg-muted/50" />
              </div>
            </div>

            <div>
              <Label>Skill Level</Label>
              <Select value={editFormData.skill_level} onValueChange={(v) => setEditFormData(prev => ({ ...prev, skill_level: v }))}>
                <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit_host_contact">Your Contact (optional)</Label>
              <Input
                id="edit_host_contact"
                type="text"
                placeholder="Phone, WhatsApp, Instagram, etc."
                value={editFormData.host_contact || ""}
                onChange={(e) => setEditFormData(prev => ({ ...prev, host_contact: e.target.value }))}
                className="bg-muted/50"
                data-testid="edit-host-contact-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Visible to players who joined the game.</p>
            </div>

            <div>
              <Label htmlFor="edit_meeting_info">Meeting Info (optional)</Label>
              <Textarea
                id="edit_meeting_info"
                placeholder="e.g. We meet at the entrance gate 10 min early, bring a light + dark shirt, parking on Maple St."
                value={editFormData.meeting_info || ""}
                onChange={(e) => setEditFormData(prev => ({ ...prev, meeting_info: e.target.value }))}
                className="bg-muted/50 min-h-[80px]"
                rows={3}
                data-testid="edit-meeting-info-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Where to meet, what to bring, how to find you. Visible to players who joined the game.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditModal(false); setEditingGame(null); }} className="border-border">Cancel</Button>
            <Button onClick={handleEditGame} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2" data-testid="save-game-btn">
              {saving ? "Saving..." : <><Save className="w-4 h-4" />Save Changes</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Modal */}
      <Dialog open={showEditProfileModal} onOpenChange={setShowEditProfileModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* Profile Picture */}
            <div className="flex flex-col items-center gap-3">
              <Avatar className="w-24 h-24 ring-2 ring-border">
                <AvatarImage src={profilePicPreview || profileStats.picture || user?.picture} />
                <AvatarFallback className="text-2xl bg-primary/20 text-primary">{getInitials(editName)}</AvatarFallback>
              </Avatar>
              <div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => document.getElementById('profile-pic-input')?.click()}
                  disabled={uploadingProfilePic}
                  data-testid="profile-pic-change-btn"
                >
                  {uploadingProfilePic ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" />Uploading...</>
                  ) : (
                    <><Camera className="w-4 h-4 mr-2" />Change Photo</>
                  )}
                </Button>
              </div>
            </div>

            {/* Username */}
            <div>
              <Label htmlFor="edit-username">Username</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
                <Input
                  id="edit-username"
                  value={editUsername}
                  onChange={(e) => setEditUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="username"
                  className="pl-8"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Only letters, numbers, and underscores</p>
            </div>

            {/* Display Name */}
            <div>
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Your display name"
              />
            </div>

            {/* Bio */}
            <div>
              <Label htmlFor="edit-bio">Bio</Label>
              <Textarea
                id="edit-bio"
                value={editBio}
                onChange={(e) => setEditBio(e.target.value)}
                placeholder="Tell us about yourself..."
                rows={3}
              />
            </div>

            {/* Sports */}
            <div>
              <Label>Sports you like</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {SPORTS_OPTIONS.map(sport => (
                  <button
                    key={sport.name}
                    type="button"
                    onClick={() => toggleSport(sport.name)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      editSports.includes(sport.name)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border hover:border-primary/50'
                    }`}
                  >
                    {sport.emoji} {sport.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Interests */}
            <div>
              <Label htmlFor="edit-custom-interests">Other interests</Label>
              <Input
                id="edit-custom-interests"
                value={editCustomInterests}
                onChange={(e) => setEditCustomInterests(e.target.value)}
                placeholder="e.g., Chess, Dancing, Martial Arts..."
              />
              <p className="text-xs text-muted-foreground mt-1">Add any interests not in the list above</p>
            </div>

            {/* Notification preferences */}
            <div className="pt-4 border-t border-border">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-3">Host notifications</p>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="notify-on-booking-row">
                <input
                  type="checkbox"
                  checked={editNotifyOnBooking}
                  onChange={(e) => setEditNotifyOnBooking(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-border accent-primary"
                  data-testid="notify-on-booking-checkbox"
                />
                <div>
                  <p className="text-sm font-medium">Email me when someone books my event</p>
                  <p className="text-xs text-muted-foreground">Instant email to {profileStats.email || "your account email"} every time a spot is reserved.</p>
                </div>
              </label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEditProfileModal(false)}>Cancel</Button>
            <Button onClick={handleSaveProfile} disabled={savingProfile}>
              {savingProfile ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Followers Modal */}
      <Dialog open={showFollowersModal} onOpenChange={setShowFollowersModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Followers ({followers.length})</DialogTitle>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto">
            {followers.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No followers yet</p>
            ) : (
              <div className="divide-y divide-border">
                {followers.map((f) => (
                  <div key={f.user_id} className="flex items-center gap-3 p-3">
                    <Avatar className="h-10 w-10">
                      {f.picture && <AvatarImage src={f.picture} />}
                      <AvatarFallback className="bg-primary/20 text-primary">{getInitials(f.name)}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium flex-1">{f.name}</span>
                    {f.user_id !== user?.user_id && (
                      <Button
                        size="sm"
                        variant={f.is_following ? "outline" : "default"}
                        onClick={() => handleFollowUser(f.user_id, f.is_following)}
                      >
                        {f.is_following ? "Following" : "Follow"}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Following Modal */}
      <Dialog open={showFollowingModal} onOpenChange={setShowFollowingModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Following ({following.length})</DialogTitle>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto">
            {following.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Not following anyone yet</p>
            ) : (
              <div className="divide-y divide-border">
                {following.map((f) => (
                  <div key={f.user_id} className="flex items-center gap-3 p-3">
                    <Avatar className="h-10 w-10">
                      {f.picture && <AvatarImage src={f.picture} />}
                      <AvatarFallback className="bg-primary/20 text-primary">{getInitials(f.name)}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium flex-1">{f.name}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleFollowUser(f.user_id, true)}
                    >
                      Unfollow
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
